import { Dispatch, SetStateAction, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// assets
import successImage from 'assets/images/success-image.svg'
import loadingCalendar from 'assets/icons/loading-calendar.png'

// local imports
import AuthUtil from 'utils/AuthUtil'
import NotificationDialog from 'components/ui/NotificationDialog'
import { IMeetingRoomSearch } from 'types/MeetingRoomType'
import { ISideBarModel } from 'types/CalendarType'
import Loader from 'components/ui/Loader'
import FormModal from 'components/ui/modal/FormModal'
import MeetingRoomSearchForm from './MeetingRoomSearchForm'
import { SearchModalHeader } from 'components/home/on-campus-services/coiffeur/coiffeur-search-modal/styled'
import MeetingResModalWrapper from './MeetingResModal'
import useGetMeetingRoomList from 'components/home/on-campus-services/meeting-room/hooks/use-get-meeting-room-list'
import useGetMeetingLocationList from 'components/home/on-campus-services/meeting-room/hooks/use-get-meeting-room-locations'
import useMeetingRoomResModal from 'components/home/on-campus-services/meeting-room/hooks/use-meeting-room-res-modal'

interface ModalProps {
  modal: boolean
  setModal: Dispatch<SetStateAction<boolean>>
  closeMeetingModal: () => void
  selectedDay: ISideBarModel | undefined
  resetAll: Dispatch<SetStateAction<boolean>>
  sidebarAfterSave: () => void
}

export default function MeetingRoomSearchModal({
  modal,
  setModal,
  selectedDay,
  resetAll,
  closeMeetingModal,
  sidebarAfterSave,
}: ModalProps) {
  const { t } = useTranslation()
  const selectedDayDate = moment(selectedDay?.day.date).toDate()
  const selectedDateStr = moment(selectedDayDate).format('DD.MM.YYYY')

  /* Get Current logged employee infos - (location id) */
  const employeeLocation = AuthUtil.getEmployeeSummary()?.locationId

  /* Filter form default values */
  const defaultValues: IMeetingRoomSearch = {
    locationId: employeeLocation ?? 0,
    meetingRoom: null,
    reservationDate: selectedDayDate,
  }
  const filteredDate = defaultValues.reservationDate

  /* Filter form state  */
  const { watch, resetField, control } = useForm<IMeetingRoomSearch>({
    defaultValues,
  })

  /* Get doctor search form values */
  const locationId = watch('locationId')
  const _selectedRoom = watch('meetingRoom')

  /* Get Meeting List - Locations | Custom Hooks */
  const { getMeetingRoomList, meetingRooms, loading } = useGetMeetingRoomList()

  const { data: locationList, isLoading: locationLoading } = useGetMeetingLocationList()

  /* Get Meeting Room List on initial page load  */
  useEffect(() => {
    getMeetingRoomList({ LocationId: locationId, ReservationDate: filteredDate })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId])

  /* Reservation Modal (Open - Close, Selected Meeting Room). | Custom Hook */
  const { openModal, closeModal, modal: isOpen, selectedRoom, notification, setNotification } = useMeetingRoomResModal()

  /* Search Modal Methods */
  const handleOnCancel = () => closeMeetingModal()
  const selectRoomAndOpen = () => {
    if (!!_selectedRoom) {
      openModal(_selectedRoom)
      setModal(false)
    }
  }

  /* Reservation Scheduler Modal */
  const handleOnCancelReservationModal = () => {
    closeModal()
    setModal(true)
  }

  /* Notification Modal */
  const handleCloseNotification = () => {
    setModal(false)
    setNotification(false)
    resetAll(false)
  }

  return (
    <>
      <FormModal
        id="meetingSelectionModal"
        closable
        header={<span>{t('meeting-res-modal.meeting-reservation')} (1/2)</span>}
        disabled={!_selectedRoom}
        onSave={selectRoomAndOpen}
        visible={modal}
        onHide={handleOnCancel}
        onCancel={handleOnCancel}>
        <SearchModalHeader style={{ marginBottom: '8px' }}>
          <div className="dialog-subheader">
            <span>{t('calendar.selected-day-info-text', { date: selectedDateStr })}</span>
          </div>
        </SearchModalHeader>

        <MeetingRoomSearchForm
          control={control}
          resetField={resetField}
          roomList={meetingRooms}
          locationList={locationList}
        />

        {loading || locationLoading ? (
          <div className="my-3 w-100">
            <Loader src={loadingCalendar} />
          </div>
        ) : null}
      </FormModal>

      {/* Meeting Room Res. Scheduler Modal */}
      {!!selectedRoom ? (
        <MeetingResModalWrapper
          selectedRoom={selectedRoom}
          open={isOpen}
          openModal={openModal}
          closeModal={closeModal}
          cancelReservationModal={handleOnCancelReservationModal}
          showNotification={() => {
            setNotification(true)
            sidebarAfterSave()
          }}
          filterDate={filteredDate}
          selectedDateStr={selectedDateStr}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('success-text.appointment-completed')}
        content={t('meeting-res-modal.notification-text')}
        firstButtonLabel={t('buttons.back-to-calendar')}
        onHide={handleCloseNotification}
        onSubmit={handleCloseNotification}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}
